import * as DomBuilder from "./dom-builder.js";
import Dataset from "../Dataset.js";
import Parsers from "../parsers/Parsers.js";
import DragAndDropUtil from "./DragAndDropUtil";
import ComponentPanel from "./ComponentPanel.js";
import ComponentDatasetsTable from "./ComponentDatasetsTable";
import ComponentDatasetForm from "./ComponentDatasetForm";
import ComponentDatasetsChart from "./ComponentDatasetsChart";

/**
 *
 * @version 2026-09-02
 * @author Patrik Harag
 */
export default class ComponentPanelDatasets extends ComponentPanel {

    /** @type {Controller} */
    #controller;

    /** @type {Map<string,Dataset>} */
    #datasets = null;
    /** @type {ComponentDatasetsTable} */
    #datasetsTable;
    /** @type {ComponentDatasetsChart} */
    #datasetsChart;

    /**
     *
     * @param controller {Controller}
     */
    constructor(controller) {
        super();
        this.#controller = controller;
        this.#datasetsTable = new ComponentDatasetsTable(controller,
                (dataset) => this.#showDeleteDialog(dataset),
                (dataset) => this.#showEditDialog(dataset));
        this.#datasetsChart = new ComponentDatasetsChart();

        this.#controller.getDataManager().addOnDatasetsLoaded(datasets => {
            let sorted = new Map([...datasets.entries()].sort((a, b) => {
                return b[1].getTransactions().length - a[1].getTransactions().length;
            }));
            this.#datasets = sorted;
            this.#refresh(sorted);
        });
    }

    getTabId() {
        return 'finance-tab-datasets';
    }

    getTabName() {
        return [
            DomBuilder.element("i", { class: "fa fa-cog" }),
            DomBuilder.span(" Datasets")
        ];
    }

    createNode() {
        let panel = DomBuilder.div({ class: "panel-data" }, [
            this.#createHelp(),
            DomBuilder.par(null, 'Loaded datasets:'),
            this.#datasetsTable.createNode(),
            DomBuilder.par({ class: 'drag-and-drop-hint' }, 'Tip: Drag and drop file here'),
            this.#datasetsChart.createNode()
        ]);

        DragAndDropUtil.initTxtFileDragAndDrop(panel, (fileName, content) => {
            this.#txtFileDropped(fileName, content);
        });

        return panel;
    }

    #createHelp() {
        let sources = DomBuilder.element("ul");
        for (let [key, parser] of Parsers.AVAILABLE) {
            sources.append(DomBuilder.element("li", null, parser.getDisplayName()));
        }

        return DomBuilder.bootstrapAlert('alert-info', [
            DomBuilder.par(null, [
                DomBuilder.element("strong", null, "Dataset"),
                " is a collection of financial transactions. There can be multiple datasets – bank statements," +
                    " savings account statements, manually created list of cash expenses, etc."
            ]),
            "Supported sources:",
            sources,

            DomBuilder.par(null, [
                DomBuilder.element("strong", null, "Multi-transactions"),
                " is a mechanism that allows splitting an existing transaction using special markup in the description (note): ",
                DomBuilder.element("code", { style: "white-space: nowrap;" }, "General text ;;; 100 ; Payment 1 ;;; 200 ; Payment 2")
            ]),
        ]);
    }

    #txtFileDropped(fileName, content) {
        let dataType = 'csv-simple';

        // select data type automatically
        let ext = fileName.split('.').pop();  // this doesn't need to be precise
        let parsers = Parsers.resolveParserByExtension(ext);
        if (parsers.length > 0) {
            dataType = parsers[0].getKey();
        }

        let dataset = new Dataset(fileName, dataType, content);
        this.#showEditDialog(dataset);
    }

    #showDeleteDialog(dataset) {
        let dialog = DomBuilder.bootstrapDialogBuilder();
        dialog.setHeaderContent('Delete dataset');
        dialog.setBodyContent(DomBuilder.par(null, "Are you sure you want to delete this dataset?"));
        dialog.addSubmitButton('Confirm', () => this.#onDatasetDeleted(dataset));
        dialog.addCloseButton('Close');
        dialog.show(this.#controller.getDialogAnchor());
    }

    #showEditDialog(dataset) {
        let formComponent = new ComponentDatasetForm();

        let dialog = DomBuilder.bootstrapDialogBuilder();
        dialog.setHeaderContent(dataset ? 'Edit dataset' : 'Add dataset');
        dialog.setBodyContent(formComponent.createNode());
        dialog.addSubmitButton('Confirm', () => this.#onDatasetChanged(dataset, formComponent.getDataset()));
        dialog.addCloseButton('Close');

        if (dataset) {
            formComponent.setRawData(dataset.getName(), dataset.getData(), dataset.getDataType(), dataset.getCurrency());
        }
        dialog.setSizeExtraLarge();
        dialog.show(this.#controller.getDialogAnchor());
    }

    #onDatasetChanged(old, dataset) {
        if (old) {
            this.#datasets.delete(old.getName());
        }
        this.#datasets.set(dataset.getName(), dataset);
        this.#controller.getDataManager().updateDatasets(this.#datasets, true, true);
    }

    #onDatasetDeleted(dataset) {
        if (this.#datasets.delete(dataset.getName())) {
            this.#controller.getDataManager().updateDatasets(this.#datasets, true, true);
        }
    }

    #refresh(datasets) {
        this.#datasetsTable.refresh(datasets);
        this.#datasetsChart.refresh(datasets);
    }
}
